/**
 * LinkedIn profile URL handling for the consultant signup (#870).
 *
 * People paste profile links in many shapes: straight from the address bar,
 * from the LinkedIn app's "Share profile" (with `?utm_*` tracking), from a
 * country subdomain (`se.linkedin.com`), with percent-encoded Nordic letters
 * (`/in/j%C3%B6rgen`), or without a scheme. All of these point at the same
 * profile, so they are accepted and stored in one canonical form.
 */

export const LINKEDIN_PROFILE_EXAMPLE = "https://www.linkedin.com/in/your-name";

export const LINKEDIN_MISSING_IN_MESSAGE =
  `Profile links look like ${LINKEDIN_PROFILE_EXAMPLE} — ` +
  "open your LinkedIn profile and copy its address";

export const LINKEDIN_MAX_LENGTH = 255;

export const LINKEDIN_TOO_LONG_MESSAGE = `LinkedIn URL must not exceed ${LINKEDIN_MAX_LENGTH} characters`;

export const LINKEDIN_INVALID_MESSAGE = `Please enter your LinkedIn profile link, e.g. ${LINKEDIN_PROFILE_EXAMPLE}`;

// Letters, digits, "-" and "_", plus percent-encoded bytes for non-ASCII names.
const SLUG = /^(?:[A-Za-z0-9_-]|%[0-9A-Fa-f]{2})+$/;

function parse(raw: string): URL | null {
  const value = raw.trim();
  if (!value) {
    return null;
  }
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`;
  try {
    const url = new URL(withScheme);
    if (url.protocol !== "https:" && url.protocol !== "http:") {
      return null;
    }
    return url;
  } catch {
    return null;
  }
}

function isLinkedInHost(host: string): boolean {
  const h = host.toLowerCase();
  return h === "linkedin.com" || h.endsWith(".linkedin.com");
}

/** The profile slug from `/in/<slug>[/...]`, or null. */
function profileSlug(url: URL): string | null {
  const parts = url.pathname.split("/").filter(Boolean);
  if (parts.length < 2 || parts[0].toLowerCase() !== "in") {
    return null;
  }
  // `new URL` percent-encodes raw non-ASCII input, so "jörgen" arrives here
  // as "j%C3%B6rgen" and still matches SLUG.
  return SLUG.test(parts[1]) ? parts[1] : null;
}

/**
 * Canonical `https://www.linkedin.com/in/<slug>` for a pasted profile link,
 * or null when it is not a LinkedIn profile link.
 */
export function normalizeLinkedInProfileUrl(raw: string | null | undefined): string | null {
  const url = parse(raw ?? "");
  if (!url || !isLinkedInHost(url.hostname)) {
    return null;
  }
  const slug = profileSlug(url);
  if (!slug) {
    return null;
  }
  // The limit applies to the stored (canonical) URL, so long tracking
  // parameters on a share link do not count against it.
  const canonical = `https://www.linkedin.com/in/${slug}`;
  return canonical.length <= LINKEDIN_MAX_LENGTH ? canonical : null;
}

/**
 * Validation message for an optional LinkedIn field, or null when the value
 * is empty or a usable profile link.
 */
export function linkedInProfileError(raw: string | null | undefined): string | null {
  if (!raw || !raw.trim()) {
    return null;
  }
  if (normalizeLinkedInProfileUrl(raw)) {
    return null;
  }
  const url = parse(raw);
  if (url && isLinkedInHost(url.hostname)) {
    return profileSlug(url) ? LINKEDIN_TOO_LONG_MESSAGE : LINKEDIN_MISSING_IN_MESSAGE;
  }
  return LINKEDIN_INVALID_MESSAGE;
}
